import { useState, useEffect, useRef, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import PolicyModal from '../components/shared/PolicyModal';
import { universities } from '../data/universities';
import InveiraLogo from '../components/shared/InveiraLogo';
// NeuronHero is a light 2D-canvas scene — imported eagerly so the hero
// headline is in the first paint (good for LCP). The robot (Hero3D) stays in
// the codebase but is no longer mounted in the hero.
import NeuronHero from '../components/home/NeuronHero';
import SceneMount from '../components/home/SceneMount';
import SceneErrorBoundary from '../components/home/SceneErrorBoundary';

gsap.registerPlugin(ScrollTrigger);

// The pinned-story laptop and the robot interviewer scene are Three.js-heavy —
// lazy-loaded so they only ship when the landing page renders AND WebGL +
// full-motion are available.
const LaptopScene = lazy(() => import('../components/home/LaptopScene'));
const Hero3D = lazy(() => import('../components/home/Hero3D'));

/* Content for the pinned scroll story (and its stacked fallback) */
const STORY_STEPS = [
  {
    n: '01',
    title: 'Build your profile',
    body: 'GPA, essays, activities, target schools. The AI reads everything before you ever sit down.',
    points: ['Narrative brief built from your essays', 'Inconsistencies flagged up front', "Each school's interview style loaded"],
  },
  {
    n: '02',
    title: 'Start your interview',
    body: "Pick a school. The AI takes on that interviewer's persona and adapts to every answer you give.",
    points: ['Adaptive follow-up questions', 'Live filler, pace and eye-contact tracking', 'Body language read on-device'],
  },
  {
    n: '03',
    title: 'Review your report',
    body: 'Every answer scored, every signal interpreted. Specific fixes — then practice again.',
    points: ['Per-question scores and transcripts', 'Presence & body-language coaching', 'Progress tracked across sessions'],
  },
];

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function webglAvailable() {
  try {
    const c = document.createElement('canvas');
    return !!(window.WebGLRenderingContext && (c.getContext('webgl2') || c.getContext('webgl')));
  } catch { return false; }
}

/* ── design tokens ─────────────────────────────────────────────────── */
const C = {
  bg:    '#07090F',
  surf:  '#0C1018',
  raise: '#111827',
  line:  'rgba(255,255,255,0.07)',
  t1:    '#EAE6DC',
  t2:    '#8A909C',
  t3:    '#5C6478', /* brightened from #464D5C for AA contrast on the dark bg */
  gold:  '#C8924A',
  blue:  '#4C82F7',
  green: '#2EC27E',
  red:   '#E5534B',
  warn:  '#E09440',
  serif: '"DM Serif Display",Georgia,serif',
  sans:  '"DM Sans",-apple-system,sans-serif',
};

const FONT = `@import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500;600&display=swap');
@keyframes up{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:none}}
.f1{animation:up .5s .05s both}.f2{animation:up .5s .15s both}.f3{animation:up .5s .25s both}

/* hero text lives in real 3D space: perspective wrapper → GSAP-scrubbed
   stage (camera-through exit) → JS-tilted layer (mouse parallax) → words
   at individual translateZ depths (closer words move more under tilt) */
.hero-persp{perspective:1100px}
.hero-stage{transform-style:preserve-3d;will-change:transform}
.hero-tilt{transform-style:preserve-3d}
.hw-wrap{display:inline-block;vertical-align:bottom;transform-style:preserve-3d;margin-right:.22em}
.hw{display:inline-block;will-change:transform;transform-style:preserve-3d}

/* gold gradient on key words — slow shimmer so it reads as light, not paint */
.gold-grad{background:linear-gradient(100deg,#EBC289 0%,#C8924A 40%,#A06E2E 60%,#EBC289 100%);background-size:220% 100%;-webkit-background-clip:text;background-clip:text;-webkit-text-fill-color:transparent;color:transparent;animation:shimmer 7s ease-in-out infinite}
@keyframes shimmer{0%,100%{background-position:0% 50%}50%{background-position:100% 50%}}

/* film grain — fixed, plain low opacity (blend modes force whole-page
   re-compositing on scroll) */
.grain{position:fixed;inset:0;z-index:70;pointer-events:none;opacity:.03;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")}

/* cursor-aware gold spotlight inside cards */
.spot-card{position:relative;overflow:hidden}
.spot-card::before{content:'';position:absolute;inset:0;border-radius:inherit;background:radial-gradient(360px circle at var(--mx,50%) var(--my,50%),rgba(200,146,74,.08),transparent 65%);opacity:0;transition:opacity .35s;pointer-events:none;z-index:0}
.spot-card:hover::before{opacity:1}
.spot-card>*{position:relative;z-index:1}

/* neuron hero — content docked to the left over a full-bleed field; the
   animation fills the whole screen behind and around it (Render.com-style).
   On mobile it centres over a simplified field instead of a strict column. */
.neuron-content{align-items:center}
.neuron-inner{width:100%;max-width:min(40vw,520px);margin-left:clamp(24px,6vw,104px);text-align:left}
.neuron-ctas{justify-content:flex-start}
@media (max-width:768px){
  .neuron-inner{max-width:520px;margin:0 auto;padding:0 24px;text-align:center}
  .neuron-ctas{justify-content:center}
  .neuron-scrim{background:radial-gradient(72% 52% at 50% 50%,rgba(7,9,15,0.72) 0%,rgba(7,9,15,0.28) 60%,rgba(7,9,15,0) 100%)!important}
}

/* robot section — animation left, statement right; stacks on narrow screens */
@media (max-width:860px){
  .robot-grid{grid-template-columns:1fr!important;gap:20px!important}
  .robot-visual{height:min(46vh,380px)!important;min-height:300px!important}
  .robot-copy{text-align:center}
  .robot-copy p{margin-left:auto!important;margin-right:auto!important}
}

/* hero scroll cue */
.scroll-cue{display:flex;flex-direction:column;align-items:center;gap:8px;color:#5C6478;font-size:10px;letter-spacing:.22em;text-transform:uppercase}
.scroll-cue .cue-line{width:1px;height:34px;background:linear-gradient(#C8924A,transparent);animation:cueDrop 1.9s cubic-bezier(.16,1,.3,1) infinite;transform-origin:top}
@keyframes cueDrop{0%{transform:scaleY(0)}45%{transform:scaleY(1)}100%{transform:scaleY(0);transform-origin:bottom}}

/* ambient hero blobs — gradient falloff instead of filter:blur (a 96px blur
   repainting under parallax was a measurable scroll-jank source) */
.amb{position:absolute;border-radius:50%;opacity:.8;pointer-events:none;will-change:transform}
.amb1{width:720px;height:720px;background:radial-gradient(circle,rgba(200,146,74,.10) 0%,rgba(200,146,74,.045) 38%,transparent 68%);top:-300px;left:-220px;animation:drift1 28s ease-in-out infinite alternate}
.amb2{width:660px;height:660px;background:radial-gradient(circle,rgba(76,130,247,.10) 0%,rgba(76,130,247,.04) 38%,transparent 68%);top:-240px;right:-240px;animation:drift2 34s ease-in-out infinite alternate}
@keyframes drift1{to{transform:translate(70px,50px)}}
@keyframes drift2{to{transform:translate(-60px,40px)}}

/* nav link underline slide */
.nav-link{position:relative}
.nav-link::after{content:'';position:absolute;left:0;bottom:-4px;width:100%;height:1.5px;background:#C8924A;transform:scaleX(0);transform-origin:left;transition:transform .25s cubic-bezier(.4,0,.2,1)}
.nav-link:hover::after{transform:scaleX(1)}

/* buttons — layered shadows, settle easing, press physics */
.magnetic-btn{transition:transform .35s cubic-bezier(.16,1,.3,1),box-shadow .35s cubic-bezier(.16,1,.3,1),background .2s}
.magnetic-btn:hover{box-shadow:0 4px 12px -4px rgba(0,0,0,.5),0 0 36px -6px rgba(76,130,247,.55),0 0 80px -20px rgba(76,130,247,.35)}
.btn-anim{transition:transform .25s cubic-bezier(.16,1,.3,1),box-shadow .3s,background .2s,border-color .2s,color .2s}
.btn-anim:hover{transform:scale(1.02)}
.btn-anim:active{transform:scale(.97)}

/* card hover lift — weightier settle, layered soft + colored shadow */
.card-lift{transition:transform .3s cubic-bezier(.16,1,.3,1),box-shadow .3s cubic-bezier(.16,1,.3,1),border-color .3s}
.card-lift:hover{transform:translateY(-5px);border-color:rgba(200,146,74,.32)!important;box-shadow:0 2px 6px rgba(0,0,0,.4),0 24px 48px -20px rgba(0,0,0,.7),0 0 32px -12px rgba(200,146,74,.3)}

/* marquee */
.marquee-track{display:inline-flex;white-space:nowrap;animation:marqueeMove 40s linear infinite;will-change:transform}
.marquee:hover .marquee-track{animation-play-state:paused}
@keyframes marqueeMove{to{transform:translateX(-50%)}}

/* "Most Popular" pricing card — continuous glow pulse. The glow lives on a
   ::before wrapper (outside the card's overflow:hidden) and pulses via OPACITY,
   which the GPU composites — instead of animating box-shadow, which forces a
   main-thread repaint of the whole blurred area every frame (measured ~6% of
   the main thread on its own while the card is on screen). */
.glow-pulse{position:relative;border-radius:18px}
.glow-pulse::before{content:'';position:absolute;inset:0;border-radius:inherit;box-shadow:0 0 50px -8px rgba(76,130,247,.6);opacity:.5;animation:glowPulse 3.2s ease-in-out infinite;pointer-events:none;will-change:opacity}
@keyframes glowPulse{0%,100%{opacity:.42}50%{opacity:.95}}

/* Off-screen animation pause: infinite keyframes (marquee, glow pulse) otherwise
   repaint/composite EVERY frame for the whole session even when scrolled far
   away — the measured cause of the page feeling heavy everywhere. A JS observer
   toggles this class so animations only run while their element is in view. */
.anim-off,.anim-off::before,.anim-off::after{animation-play-state:paused!important}

/* sticky nav is 54px — anchored sections must not scroll underneath it */
section[data-anchor]{scroll-margin-top:70px}

/* pricing "Most Popular": subtle 3D tilt on hover (CSS perspective) */
.tilt-card{transform-style:preserve-3d;transition:transform .25s ease}

/* responsive: collapse the fixed multi-column grids on small screens */
@media (max-width: 760px){
  .resp-grid-3{grid-template-columns:1fr!important}
  .resp-grid-2{grid-template-columns:1fr!important}
  .resp-flex{flex-direction:column!important}
  .steps-line{display:none}
  .uni-grid{grid-template-columns:1fr!important}
}

/* reduced motion: kill everything decorative */
@media (prefers-reduced-motion: reduce){
  *,*::before,*::after{animation-duration:.01ms!important;animation-iteration-count:1!important;transition-duration:.01ms!important;scroll-behavior:auto!important}
  .marquee-track{animation:none}
  .amb{animation:none}
}`;

const QUESTIONS = [
  '"What drives your interest in economics specifically?"',
  '"Tell me about a time your values were genuinely tested."',
  '"What would you study if grades did not exist?"',
  '"Describe a moment you changed someone else\'s mind."',
  '"What is something you believe most people would disagree with?"',
];
const NAMES = ['Dr. Sarah Chen', 'Prof. James Okafor', 'Ms. Emily Torres', 'Dr. Raj Patel'];

function uniIntensityColor(pct) {
  if (pct >= 85) return C.red;
  if (pct >= 75) return C.warn;
  if (pct >= 65) return C.gold;
  return C.green;
}
function uniIntensityLabel(pct) {
  if (pct >= 85) return 'Very high';
  if (pct >= 75) return 'High';
  if (pct >= 65) return 'Moderate-high';
  return 'Moderate';
}

/* ── motion helpers ─────────────────────────────────────────────────── */

// Number that counts up when it scrolls into view
function Counter({ to, suffix = '', style }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (prefersReducedMotion()) { el.textContent = `${to}${suffix}`; return; }
    const obs = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      obs.disconnect();
      const t0 = performance.now();
      const tick = (t) => {
        const p = Math.min(1, (t - t0) / 1200);
        el.textContent = `${Math.round(to * (1 - Math.pow(1 - p, 3)))}${suffix}`;
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }, { threshold: 0.6 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [to, suffix]);
  return <span ref={ref} style={style}>0{suffix}</span>;
}

// Infinite university-name marquee, pauses on hover
function Marquee() {
  const names = ['Harvard', 'Oxford', 'MIT', 'Stanford', 'Cambridge', 'Yale', 'Princeton', 'Imperial', 'Columbia', 'LSE', 'Brown', 'Duke', 'Cornell', 'UCL'];
  return (
    <div className="marquee" aria-hidden="true" style={{
      overflow: 'hidden', borderTop: `1px solid ${C.line}`, borderBottom: `1px solid ${C.line}`,
      padding: '16px 0', marginBottom: 'clamp(96px, 11vw, 136px)',
      maskImage: 'linear-gradient(90deg,transparent,black 12%,black 88%,transparent)',
      WebkitMaskImage: 'linear-gradient(90deg,transparent,black 12%,black 88%,transparent)',
    }}>
      <div className="marquee-track">
        {[0, 1].map(k => (
          <div key={k} style={{ display: 'inline-flex' }}>
            {names.map(n => (
              <span key={`${n}${k}`} style={{ fontFamily: C.serif, fontSize: 17, color: C.t3, padding: '0 14px', whiteSpace: 'nowrap' }}>
                {n}<span style={{ color: 'rgba(200,146,74,0.45)', paddingLeft: 28 }}>·</span>
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

const ROSTER = [
  { name: 'Alex K.',  uni: 'Harvard',  score: 82, trend: '+4' },
  { name: 'Mia P.',   uni: 'Stanford', score: 74, trend: '+1' },
  { name: 'James O.', uni: 'Yale',     score: 67, trend: '-2' },
  { name: 'Sofia R.', uni: 'MIT',      score: 91, trend: '+7' },
];

/* ── shared sub-components ─────────────────────────────────────────── */
function BulletCheck({ text }) {
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
      <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'rgba(76,130,247,0.12)', border: '1px solid rgba(76,130,247,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 10, color: C.blue }}>✓</div>
      <span style={{ fontSize: 13, color: C.t2 }}>{text}</span>
    </div>
  );
}

/* ── step panel content ─────────────────────────────────────────────── */
function StepPanel0() {
  const items = ['GPA (weighted / unweighted)', 'SAT / ACT score', 'Personal statement', 'Extracurricular activities', 'Target universities'];
  return (
    <div className="resp-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
      <div style={{ padding: 22, borderRight: `1px solid ${C.line}` }}>
        <div style={{ fontSize: 11, color: C.t3, letterSpacing: '.07em', textTransform: 'uppercase', marginBottom: 12 }}>What you fill in</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          {items.map(item => (
            <div key={item} style={{ display: 'flex', alignItems: 'center', gap: 9, background: C.raise, border: `1px solid rgba(255,255,255,0.06)`, borderRadius: 8, padding: '9px 12px' }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.green, flexShrink: 0, display: 'inline-block' }} />
              <span style={{ fontSize: 13, color: C.t1 }}>{item}</span>
            </div>
          ))}
        </div>
      </div>
      <div style={{ padding: 22 }}>
        <div style={{ fontSize: 11, color: C.t3, letterSpacing: '.07em', textTransform: 'uppercase', marginBottom: 12 }}>What the AI builds</div>
        <div style={{ background: C.raise, border: '1px solid rgba(76,130,247,0.2)', borderRadius: 10, padding: 14, marginBottom: 10 }}>
          <div style={{ fontSize: 11, color: C.blue, marginBottom: 6 }}>Narrative profile</div>
          <div style={{ fontSize: 12.5, color: C.t2, lineHeight: 1.6 }}>A pre-interview brief from your essays. Highlights themes, flags inconsistencies, primes the AI.</div>
        </div>
        <div style={{ background: C.raise, border: '1px solid rgba(200,146,74,0.2)', borderRadius: 10, padding: 14 }}>
          <div style={{ fontSize: 11, color: C.gold, marginBottom: 6 }}>University context</div>
          <div style={{ fontSize: 12.5, color: C.t2, lineHeight: 1.6 }}>Each school's interview style, duration, and what interviewers specifically look for.</div>
        </div>
      </div>
    </div>
  );
}

function StepPanel1() {
  return (
    <div className="resp-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
      <div style={{ padding: 22, borderRight: `1px solid ${C.line}` }}>
        <div style={{ fontSize: 11, color: C.t3, letterSpacing: '.07em', textTransform: 'uppercase', marginBottom: 14 }}>Live interview room</div>
        <div style={{ background: C.raise, border: `1px solid ${C.line}`, borderRadius: 10, padding: 14, marginBottom: 10 }}>
          <div style={{ fontSize: 10, color: C.t3, marginBottom: 7 }}>Current question</div>
          <div style={{ fontSize: 13, color: C.t1, lineHeight: 1.6, fontStyle: 'italic' }}>"Tell me about a time your values were genuinely tested."</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 7 }}>
          {[{ label: 'Filler', val: '3', color: C.green }, { label: 'Eye', val: '89%', color: C.gold }, { label: 'Pace', val: '141', color: C.blue }].map(m => (
            <div key={m.label} style={{ background: C.raise, border: `1px solid rgba(255,255,255,0.06)`, borderRadius: 8, padding: 9, textAlign: 'center' }}>
              <div style={{ fontSize: 10, color: C.t3, marginBottom: 4 }}>{m.label}</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: m.color, fontFamily: C.serif }}>{m.val}</div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ padding: 22 }}>
        <div style={{ fontSize: 11, color: C.t3, letterSpacing: '.07em', textTransform: 'uppercase', marginBottom: 14 }}>How questions adapt</div>
        {[
          { label: 'You say', labelColor: C.t2, text: 'You mention leading a debate club' },
          { label: 'AI decides', labelColor: C.blue, text: 'Worth probing deeper' },
          { label: 'AI asks', labelColor: C.gold, text: '"Tell me about a decision others disagreed with."', italic: true, textColor: C.t1 },
        ].map((row, i, arr) => (
          <div key={row.label} style={{ display: 'flex', gap: 10, padding: '10px 0', borderBottom: i < arr.length - 1 ? `1px solid rgba(255,255,255,0.05)` : 'none' }}>
            <span style={{ fontSize: 10, color: row.labelColor, fontWeight: 600, minWidth: 62 }}>{row.label}</span>
            <span style={{ fontSize: 12.5, color: row.textColor || C.t2, lineHeight: 1.55, fontStyle: row.italic ? 'italic' : 'normal' }}>{row.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StepPanel2() {
  const scores = [
    { label: 'Content quality', weight: '40%', val: 74, color: C.blue },
    { label: 'Delivery',        weight: '25%', val: 58, color: C.red  },
    { label: 'University fit',  weight: '20%', val: 68, color: C.gold },
    { label: 'Consistency',     weight: '15%', val: 80, color: C.green },
  ];
  const questions = [
    { q: 'Tell me about a time your values were tested', score: 89, color: C.green },
    { q: 'Why Harvard specifically?',                   score: 68, color: C.warn  },
    { q: 'Walk me through your leadership role',        score: 75, color: C.warn  },
  ];
  return (
    <div className="resp-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
      <div style={{ padding: 22, borderRight: `1px solid ${C.line}` }}>
        <div style={{ fontSize: 11, color: C.t3, letterSpacing: '.07em', textTransform: 'uppercase', marginBottom: 14 }}>Score breakdown</div>
        {scores.map(s => (
          <div key={s.label} style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
              <span style={{ fontSize: 12, color: C.t2 }}>{s.label} <span style={{ color: C.t3 }}>{s.weight}</span></span>
              <span style={{ fontFamily: C.serif, fontSize: 16, color: s.color }}>{s.val}</span>
            </div>
            <div style={{ height: 3, background: C.raise, borderRadius: 2, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${s.val}%`, background: s.color, borderRadius: 2 }} />
            </div>
          </div>
        ))}
      </div>
      <div style={{ padding: 22 }}>
        <div style={{ fontSize: 11, color: C.t3, letterSpacing: '.07em', textTransform: 'uppercase', marginBottom: 14 }}>Per-question detail</div>
        {questions.map((q, i, arr) => (
          <div key={q.q} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: i < arr.length - 1 ? `1px solid rgba(255,255,255,0.05)` : 'none' }}>
            <div style={{ flex: 1, fontSize: 12.5, color: C.t2, lineHeight: 1.45 }}>{q.q}</div>
            <div style={{ fontFamily: C.serif, fontSize: 20, fontWeight: 700, color: q.color, minWidth: 28, textAlign: 'right' }}>{q.score}</div>
          </div>
        ))}
        <div style={{ marginTop: 10, fontSize: 12, color: C.t3 }}>Click any question to see transcript, what worked, and what to fix.</div>
      </div>
    </div>
  );
}

const STEP_PANELS = [<StepPanel0 />, <StepPanel1 />, <StepPanel2 />];

/* ══════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════════════ */
export default function Home() {
  const navigate = useNavigate();

  const howRef      = useRef(null);
  const unisRef     = useRef(null);
  const schoolsRef  = useRef(null);

  const [activeStep, setActiveStep] = useState(0);
  const [activeUni,  setActiveUni]  = useState('Harvard University');
  const [uniTab,     setUniTab]     = useState('Ivy League');
  const [uniSearch,  setUniSearch]  = useState('');
  const [policyModal, setPolicyModal] = useState(null);

  const [mockQ,     setMockQ]     = useState(0);
  const [mockN,     setMockN]     = useState(0);
  const [mockQNum,  setMockQNum]  = useState(3);
  const [mockFill,  setMockFill]  = useState(2);
  const [mockEye,   setMockEye]   = useState(91);
  const [mockDur,   setMockDur]   = useState('1:24');
  const [qVisible,  setQVisible]  = useState(true);

  useEffect(() => {
    const iv = setInterval(() => {
      setQVisible(false);
      setTimeout(() => {
        setMockQ(q => (q + 1) % QUESTIONS.length);
        setMockN(n => (n + 1) % NAMES.length);
        setMockQNum(Math.floor(Math.random() * 6) + 2);
        setMockFill(Math.floor(Math.random() * 9));
        setMockEye(Math.floor(74 + Math.random() * 22));
        setMockDur(Math.floor(Math.random()*3)+':'+String(Math.floor(Math.random()*60)).padStart(2,'0'));
        setQVisible(true);
      }, 280);
    }, 3500);
    return () => clearInterval(iv);
  }, []);

  const scrollTo = (ref) => {
    const el = ref.current;
    if (!el) return;
    if (lenisRef.current) lenisRef.current.scrollTo(el, { offset: -70, duration: 1.2 });
    else el.scrollIntoView({ behavior: 'smooth' });
  };

  // Click-to-jump for the pinned story's step indicator. The story timeline
  // holds each step fully-visible over a progress window (step 0 ≈ 0–0.28,
  // step 1 ≈ 0.40–0.61, step 2 ≈ 0.74–1.0); these settle points sit inside each
  // window and inside its stage band (<0.33 / <0.66 / else). We map progress to
  // an absolute scroll position via the ScrollTrigger's real start/end, then let
  // the existing scrub reflect it — no separate animation to keep in sync.
  const STORY_SETTLE = [0.06, 0.47, 0.82];
  const jumpToStoryStep = (i) => {
    const st = storyStRef.current;
    if (!st) return;
    const targetY = st.start + STORY_SETTLE[i] * (st.end - st.start);
    if (lenisRef.current) lenisRef.current.scrollTo(targetY, { duration: 1.2 });
    else window.scrollTo({ top: targetY, behavior: 'smooth' });
  };

  /* ── motion setup ── */
  const rootRef = useRef(null);
  const [navScrolled, setNavScrolled] = useState(false);
  // Neuron hero: the pinned scroll-through fly-in runs with full motion on
  // ≥768px. Below that (and for reduced motion) it renders as a single
  // non-pinned screen — a lighter idle field, or a static frame.
  const [heroPinned] = useState(() =>
    !prefersReducedMotion() && window.innerWidth >= 768);
  const [heroMobile] = useState(() => window.innerWidth < 768);
  // Low-end tier: few CPU cores usually tracks weaker GPUs (older laptops,
  // budget phones). Serve a lighter neuron field to these devices.
  const [heroLowPower] = useState(() => (navigator.hardwareConcurrency || 8) <= 4);
  // Robot interviewer scene (Hero3D) — WebGL-heavy, shown after the hero on
  // capable desktop-ish viewports; skipped for reduced motion / no WebGL.
  const [robot3D] = useState(() =>
    webglAvailable() && !prefersReducedMotion() && window.innerWidth >= 768);
  // Pinned scroll story needs more room + pointer scrolling; below 1024px
  // (and for reduced motion / no WebGL) the classic stacked version renders.
  const [storyMode] = useState(() =>
    webglAvailable() && !prefersReducedMotion() && window.innerWidth >= 1024);
  const storyProgress = useRef(0);
  // The pinned story's ScrollTrigger — kept so the step indicator can compute
  // the exact scroll position for each step and jump there on click.
  const storyStRef = useRef(null);
  // Scroll 0→1 across the pinned neuron hero, written by ScrollTrigger and
  // read inside NeuronHero's draw loop to push the camera through the field.
  const heroProgress = useRef(0);

  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // The 3D scenes' delayed "pop-in" (seconds before the robot/laptop appear)
  // is the ~0.9MB Three.js chunk downloading when the user arrives. EAGERLY
  // download it shortly after first paint — NOT on requestIdleCallback, which
  // on a slow cold load may never fire before the user scrolls, so the chunk
  // downloads on arrival. Combined with SceneMount's wide mount-lead, the scene
  // then mounts and compiles its shaders off-screen while the user is still
  // scrolling the hero, so it's already visible by the time it's on screen.
  useEffect(() => {
    if (!robot3D) return;   // no WebGL scenes on this device
    const dl = setTimeout(() => {
      import('../components/home/Hero3D').catch(() => {});
      import('../components/home/LaptopScene').catch(() => {});
    }, 250);
    return () => clearTimeout(dl);
  }, [robot3D]);

  /* Lenis smooth scroll — landing page only, torn down on unmount */
  const lenisRef = useRef(null);
  useEffect(() => {
    if (prefersReducedMotion()) return;
    let raf, cancelled = false;
    let tick;
    import('lenis').then(({ default: Lenis }) => {
      if (cancelled) return;
      // lerp mode (exponential, ~native feel) instead of the old duration:1.05
      // quartic drift — that heavy smoothing made scrolling feel rubbery/laggy
      // even at 60fps. 0.1 stays smooth but tracks wheel/trackpad input tightly.
      const lenis = new Lenis({ lerp: 0.1, wheelMultiplier: 1 });
      lenisRef.current = lenis;
      // Single clock: drive Lenis from GSAP's ticker and keep ScrollTrigger in
      // lockstep — two rAF loops desync and read as scroll jank.
      lenis.on('scroll', ScrollTrigger.update);
      tick = (time) => lenis.raf(time * 1000);
      gsap.ticker.add(tick);
      gsap.ticker.lagSmoothing(0);
    });
    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
      if (tick) gsap.ticker.remove(tick);
      lenisRef.current?.destroy();
      lenisRef.current = null;
    };
  }, []);

  /* Pause infinite CSS animations while their element is off-screen. Otherwise
     shimmer / marquee / glow-pulse repaint every frame for the whole session
     regardless of scroll position — the measured cause of the page feeling
     "heavy everywhere" (idle main-thread ~9.6% vs ~1% for a normal page). They
     resume the moment their section scrolls into view. */
  useEffect(() => {
    if (prefersReducedMotion()) return;
    const els = rootRef.current?.querySelectorAll('.gold-grad, .marquee-track, .glow-pulse, .amb, .cue-line');
    if (!els?.length) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => e.target.classList.toggle('anim-off', !e.isIntersecting));
    }, { rootMargin: '150px' });
    els.forEach(el => { el.classList.add('anim-off'); io.observe(el); }); // start paused; observer un-pauses the visible ones
    return () => io.disconnect();
  }, []);

  /* Cursor-aware spotlight on cards (single delegated listener) */
  useEffect(() => {
    const root = rootRef.current;
    if (!root || prefersReducedMotion()) return;
    const onMove = (e) => {
      const card = e.target.closest?.('.spot-card');
      if (!card) return;
      const r = card.getBoundingClientRect();
      card.style.setProperty('--mx', `${e.clientX - r.left}px`);
      card.style.setProperty('--my', `${e.clientY - r.top}px`);
    };
    root.addEventListener('mousemove', onMove, { passive: true });
    return () => root.removeEventListener('mousemove', onMove);
  }, []);

  /* Reveal safety net: content must NEVER stay invisible. If a group is in
     the viewport and its items are still hidden 1s later (ScrollTrigger
     mis-measured, e.g. layout shifted after lazy loads), force them visible. */
  useEffect(() => {
    if (prefersReducedMotion()) return; // nothing is ever hidden in this mode
    const timers = [];
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        obs.unobserve(entry.target);
        timers.push(setTimeout(() => {
          entry.target.querySelectorAll('[data-reveal]').forEach(el => {
            if (parseFloat(window.getComputedStyle(el).opacity) < 0.9) {
              gsap.to(el, { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out', overwrite: 'auto', clearProps: 'transform' });
            }
          });
        }, 1000));
      });
    }, { threshold: 0.05 });
    rootRef.current?.querySelectorAll('[data-reveal-group]').forEach(g => obs.observe(g));
    // re-measure trigger positions once everything (fonts, lazy 3D) has loaded
    const refresh = () => ScrollTrigger.refresh();
    window.addEventListener('load', refresh);
    const t = setTimeout(refresh, 800);
    return () => { obs.disconnect(); timers.forEach(clearTimeout); clearTimeout(t); window.removeEventListener('load', refresh); };
  }, []);

  useEffect(() => {
    if (prefersReducedMotion()) return; // all content stays at natural, visible state
    const ctx = gsap.context(() => {
      // Motion discipline: one settle ease, durations from a 0.2/0.4/0.6/0.8 scale
      gsap.defaults({ ease: 'expo.out' });

      // Neuron hero: scroll across the pinned runway writes 0→1 into
      // heroProgress, read inside NeuronHero's draw loop to fly the camera
      // through the field and dissolve the overlay copy as it enters. The
      // component owns all its own animation — this just feeds it progress.
      const neuronRunway = rootRef.current?.querySelector('.neuron-runway');
      if (neuronRunway) {
        ScrollTrigger.create({
          trigger: neuronRunway,
          start: 'top top',
          end: 'bottom bottom',
          onUpdate: self => { heroProgress.current = self.progress; },
        });
      }

      // Section headlines: clip-path wipe
      gsap.utils.toArray('.wipe-headline').forEach(el => {
        gsap.fromTo(el,
          { clipPath: 'inset(0 0 100% 0)', y: 22 },
          { clipPath: 'inset(0 0 -8% 0)', y: 0, duration: 0.8, ease: 'power3.out',
            scrollTrigger: { trigger: el, start: 'top 86%' } });
      });

      // Card groups: fade + lift, staggered. `once: true` + an early start so
      // a fired trigger can never re-hide content; the IntersectionObserver
      // safety net (above) covers the mis-measured-layout case.
      gsap.utils.toArray('[data-reveal-group]').forEach(group => {
        const items = group.querySelectorAll('[data-reveal]');
        if (!items.length) return;
        gsap.from(items, {
          y: 36, opacity: 0, duration: 0.8, stagger: 0.1,
          scrollTrigger: { trigger: group, start: 'top 92%', once: true },
        });
      });

      // Depth: background layers drift at different speeds than content
      gsap.utils.toArray('[data-parallax]').forEach(el => {
        const speed = parseFloat(el.dataset.parallax || '0');
        gsap.to(el, {
          y: () => speed * 320, ease: 'none',
          // scrub:true adds ~1s smoothing; a small numeric scrub tracks scroll tightly
          scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: 0.3 },
        });
      });

      // Interview mockup: scale + rotation settle, then inner elements
      gsap.from('.mockup-shell', {
        scale: 0.95, rotation: -0.6, y: 36, opacity: 0, duration: 0.9, ease: 'power3.out',
        transformOrigin: '50% 100%',
        scrollTrigger: { trigger: '.mockup-shell', start: 'top 86%' },
      });
      gsap.from('.mockup-shell [data-mock-item]', {
        y: 14, opacity: 0, duration: 0.5, stagger: 0.08, delay: 0.3, ease: 'power2.out',
        scrollTrigger: { trigger: '.mockup-shell', start: 'top 86%' },
      });


      // How-it-works: connecting line draws itself between the steps
      // (stacked fallback mode only)
      const line = rootRef.current?.querySelector('.steps-line path');
      if (line) {
        const len = line.getTotalLength();
        gsap.fromTo(line, { strokeDasharray: len, strokeDashoffset: len }, {
          strokeDashoffset: 0, duration: 1.4, ease: 'power2.inOut',
          scrollTrigger: { trigger: '.steps-grid', start: 'top 78%' },
        });
      }

      // Pinned scroll story: a 300vh runway with a sticky stage. One scrubbed
      // timeline crossfades the step text; the same progress value drives the
      // 3D laptop via storyProgress (read in useFrame — zero React renders).
      const runway = rootRef.current?.querySelector('.story-runway');
      if (runway) {
        const tl = gsap.timeline({ paused: true, defaults: { ease: 'none' } });
        tl.to({}, { duration: 1 }); // 0..1 spine
        tl.to('.story-step-0', { opacity: 0, y: -26, duration: 0.06 }, 0.28)
          .fromTo('.story-step-1', { opacity: 0, y: 26 }, { opacity: 1, y: 0, duration: 0.06 }, 0.345)
          .to('.story-step-1', { opacity: 0, y: -26, duration: 0.06 }, 0.61)
          .fromTo('.story-step-2', { opacity: 0, y: 26 }, { opacity: 1, y: 0, duration: 0.06 }, 0.675);
        // stage dots
        tl.to('.story-dot-0', { opacity: 0.25, duration: 0.04 }, 0.33)
          .to('.story-dot-1', { opacity: 1, duration: 0.04 }, 0.33)
          .to('.story-dot-1', { opacity: 0.25, duration: 0.04 }, 0.66)
          .to('.story-dot-2', { opacity: 1, duration: 0.04 }, 0.66);
        storyStRef.current = ScrollTrigger.create({
          trigger: runway,
          start: 'top top',
          end: 'bottom bottom',
          scrub: 0.35,
          animation: tl,
          onUpdate: self => {
            storyProgress.current = self.progress;
          },
        });
      }
    }, rootRef);
    return () => ctx.revert();
  }, []);

  const tabUnis = universities.filter(u => {
    if (uniTab === 'Ivy League') return u.type === 'Ivy League';
    if (uniTab === 'Top US') return u.country === 'US' && u.type !== 'Ivy League';
    return u.country === 'UK';
  });
  const searchTerm = uniSearch.trim().toLowerCase();
  const visibleUnis = searchTerm ? universities.filter(u => u.name.toLowerCase().includes(searchTerm)) : tabUnis;
  const uData = universities.find(u => u.name === activeUni) || universities[0];
  const uInfo = {
    pct:   uData.intensity,
    color: uniIntensityColor(uData.intensity),
    label: uniIntensityLabel(uData.intensity),
    style: `${uData.style} · ${uData.duration}`,
  };

  /* ── nav styles ── */
  const navBtn = { color: C.t2, fontSize: 13, cursor: 'pointer', background: 'none', border: 'none', fontFamily: C.sans, transition: 'color .15s' };
  const btnP   = { background: C.blue, color: '#fff', border: 'none', borderRadius: 9, padding: '10px 22px', fontFamily: C.sans, fontSize: 13, fontWeight: 500, cursor: 'pointer' };
  const btnG   = { background: 'transparent', color: C.t2, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 9, padding: '10px 18px', fontFamily: C.sans, fontSize: 13, cursor: 'pointer' };

  return (
    <div ref={rootRef} style={{
      // layered near-blacks: faint blue wash from above, faint gold at right —
      // depth instead of one flat hex
      background: `radial-gradient(1100px 560px at 50% -120px, rgba(76,130,247,0.055), transparent 60%),
                   radial-gradient(880px 520px at 88% 38%, rgba(200,146,74,0.035), transparent 60%),
                   radial-gradient(900px 600px at 8% 78%, rgba(76,130,247,0.03), transparent 60%),
                   #05070C`,
      // overflow-x: CLIP, not hidden — `hidden` computes overflow-y to auto,
      // which silently breaks position:sticky for every descendant (the
      // pinned story stage never stuck; users saw a blank runway). `clip`
      // clips without creating a scroll container.
      color: C.t1, fontFamily: C.sans, WebkitFontSmoothing: 'antialiased', overflowX: 'clip',
    }}>
      <style>{FONT}</style>
      {/* film grain overlay */}
      <div className="grain" aria-hidden="true" />

      {/* ── NAV — sticky, blur intensifies on scroll ── */}
      <nav style={{
        position: 'sticky', top: 0, zIndex: 50,
        height: 54, padding: '0 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        borderBottom: `1px solid ${navScrolled ? 'rgba(255,255,255,0.09)' : C.line}`,
        background: navScrolled ? 'rgba(7,9,15,0.86)' : 'rgba(7,9,15,0.5)',
        backdropFilter: navScrolled ? 'blur(10px)' : 'blur(4px)',
        WebkitBackdropFilter: navScrolled ? 'blur(10px)' : 'blur(4px)',
        transition: 'background .3s ease, backdrop-filter .3s ease, border-color .3s ease',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <InveiraLogo size={22} dark={true} />
          <span style={{ fontFamily: C.serif, fontSize: 20, color: C.gold }}>Inveira</span>
        </div>
        <div style={{ display: 'flex', gap: 26 }}>
          {[
            { label: 'How it works', ref: howRef },
            { label: 'Universities',  ref: unisRef },
            { label: 'For schools',   ref: schoolsRef },
          ].map(({ label, ref }) => (
            <button key={label} className="nav-link" style={navBtn} onClick={() => scrollTo(ref)}
              onMouseEnter={e => { e.currentTarget.style.color = C.t1; }}
              onMouseLeave={e => { e.currentTarget.style.color = C.t2; }}
            >{label}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-anim" style={{ ...btnG, padding: '8px 16px' }} onClick={() => navigate('/login')}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.22)'; e.currentTarget.style.color = C.t1; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';  e.currentTarget.style.color = C.t2; }}
          >Sign in</button>
          <button className="btn-anim" style={{ ...btnP, padding: '8px 18px' }} onClick={() => navigate('/signup')}
            onMouseEnter={e => { e.currentTarget.style.background = '#3568D4'; }}
            onMouseLeave={e => { e.currentTarget.style.background = C.blue; }}
          >Start free</button>
        </div>
      </nav>

      {/* ── HERO — scroll-driven neuron field (ported from the Nocturne
             reference, in Inveira's blue palette). Full motion pins a tall
             runway and flies the camera through the field; reduced-motion /
             mobile render a single non-pinned screen. ── */}
      {heroPinned ? (
        <div className="neuron-runway" style={{ height: '280vh', position: 'relative', background: C.bg }}>
          <div style={{ position: 'sticky', top: 0, height: '100vh', overflow: 'hidden' }}>
            <SceneErrorBoundary label="neuron">
              <NeuronHero
                progressRef={heroProgress}
                lowPower={heroLowPower}
                onPrimary={() => navigate('/signup')}
                onSecondary={() => navigate('/login')}
              />
            </SceneErrorBoundary>
          </div>
        </div>
      ) : (
        <section style={{ position: 'relative', height: '100vh', minHeight: 560, overflow: 'hidden', background: C.bg }}>
          <SceneErrorBoundary label="neuron">
            <NeuronHero
              animate={!prefersReducedMotion()}
              mobile={heroMobile}
              lowPower={heroLowPower}
              onPrimary={() => navigate('/signup')}
              onSecondary={() => navigate('/login')}
            />
          </SceneErrorBoundary>
        </section>
      )}



      {/* ── ROBOT INTERVIEWER (Hero3D) — animation LEFT, statement RIGHT. The
             scene is mount-gated so its WebGL context only exists near view. ── */}
      {robot3D && (
        <section className="robot-section" style={{ position: 'relative', padding: 'clamp(36px, 6vh, 90px) clamp(24px, 5vw, 72px) clamp(88px, 12vw, 140px)', maxWidth: 1280, margin: '0 auto' }}>
          <div className="robot-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 56fr) minmax(0, 44fr)', alignItems: 'center', gap: 'clamp(24px, 4vw, 76px)' }}>
            {/* LEFT — the robot scene */}
            <SceneMount label="robot" className="robot-visual" style={{ width: '100%', height: 'min(64vh, 540px)', minHeight: 360 }}>
              {(onReady) => <Hero3D onReady={onReady} />}
            </SceneMount>

            {/* RIGHT — spaced-out statement, staggered reveal on scroll-in */}
            <div className="robot-copy" data-reveal-group>
              <div data-reveal style={{ fontSize: 10.5, color: C.gold, letterSpacing: '.16em', textTransform: 'uppercase', marginBottom: 22, opacity: 0.9 }}>Your AI interviewer</div>
              <h2 data-reveal style={{ fontFamily: C.serif, fontWeight: 400, fontSize: 'clamp(30px, 3.3vw, 50px)', lineHeight: 1.06, letterSpacing: '-.02em', color: C.t1, margin: '0 0 26px' }}>
                A real interview,<br />run by AI.
              </h2>
              <p data-reveal style={{ fontSize: 'clamp(15px, 1.15vw, 17px)', color: C.t2, lineHeight: 1.75, letterSpacing: '.005em', margin: '0 0 18px', maxWidth: 440 }}>
                It asks, listens, and follows up on every answer — picking up on what you actually say, the way a real interviewer would.
              </p>
              <p data-reveal style={{ fontSize: 'clamp(15px, 1.15vw, 17px)', color: C.t2, lineHeight: 1.75, letterSpacing: '.005em', margin: 0, maxWidth: 440 }}>
                When it's over, you get a full report built around exactly how you performed.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* ── HOW IT WORKS ── */}
      <section ref={howRef} data-anchor style={{ padding: storyMode ? '0 0 clamp(112px, 13vw, 152px)' : '0 28px clamp(112px, 13vw, 152px)', maxWidth: storyMode ? 'none' : 860, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: storyMode ? 0 : 28, padding: storyMode ? '0 28px' : 0 }}>
          <div style={{ fontSize: 10, color: C.t3, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 10 }}>How it works</div>
          <h2 className="wipe-headline" style={{ fontFamily: C.serif, fontSize: 38, color: C.t1, marginBottom: 8 }}>Three steps, one goal.</h2>
          <p style={{ fontSize: 14, color: C.t2 }}>{storyMode ? 'Keep scrolling — the story plays as you go.' : 'Click each step to see what it looks like inside.'}</p>
        </div>

        {storyMode ? (
          /* Pinned scroll story: 300vh runway, sticky stage, scrubbed by scroll.
             Scrolling back up scrubs the whole story in reverse. */
          <div className="story-runway" style={{ height: '300vh', position: 'relative' }}>
            <div style={{ position: 'sticky', top: 0, height: '100vh', display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.15fr', gap: 28, alignItems: 'center', width: '100%', maxWidth: 1180, margin: '0 auto', padding: '0 48px' }}>
                {/* left: crossfading step text */}
                <div style={{ position: 'relative', minHeight: 360 }}>
                  {STORY_STEPS.map((s, i) => (
                    <div key={s.n} className={`story-step-${i}`} style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', opacity: i === 0 ? 1 : 0 }}>
                      <div style={{ fontFamily: C.serif, fontSize: 16, color: C.gold, marginBottom: 14 }}>{s.n}</div>
                      <h3 style={{ fontFamily: C.serif, fontSize: 'clamp(30px, 3.2vw, 44px)', color: C.t1, margin: '0 0 14px', lineHeight: 1.1, letterSpacing: '-.02em' }}>{s.title}</h3>
                      <p style={{ fontSize: 15.5, color: C.t2, lineHeight: 1.75, margin: '0 0 22px', maxWidth: 390 }}>{s.body}</p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {s.points.map(pt => (
                          <div key={pt} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                            <span style={{ color: C.gold, fontSize: 13, lineHeight: 1.5 }}>—</span>
                            <span style={{ fontSize: 13.5, color: C.t2, lineHeight: 1.5 }}>{pt}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  {/* stage progress dashes — clickable: jump to that step.
                      The dash keeps its story-dot-N class so the scrubbed
                      timeline still drives its active opacity; the number label
                      above makes it read as a step and gives a clear hit target. */}
                  <div style={{ position: 'absolute', left: 0, bottom: 0, display: 'flex', gap: 18 }}>
                    {STORY_STEPS.map((s, i) => (
                      <button
                        key={s.n}
                        type="button"
                        onClick={() => jumpToStoryStep(i)}
                        aria-label={`Go to step ${s.n} — ${s.title}`}
                        title={`${s.n} · ${s.title}`}
                        style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start', background: 'none', border: 'none', padding: '4px 0', margin: 0, cursor: 'pointer' }}
                        onMouseEnter={e => { const n = e.currentTarget.querySelector('.story-num'); n.style.color = C.gold; n.style.textDecoration = 'underline'; }}
                        onMouseLeave={e => { const n = e.currentTarget.querySelector('.story-num'); n.style.color = C.t3; n.style.textDecoration = 'none'; }}
                      >
                        <span className="story-num" style={{ fontFamily: C.serif, fontSize: 12, color: C.t3, letterSpacing: '.04em', textUnderlineOffset: 3, transition: 'color .18s' }}>{s.n}</span>
                        <span className={`story-dot-${i}`} style={{ width: 26, height: 2, background: C.gold, opacity: i === 0 ? 1 : 0.25, display: 'inline-block', borderRadius: 1, transition: 'opacity .18s' }} />
                      </button>
                    ))}
                  </div>
                </div>
                {/* right: the 3D laptop, scrubbed by the same scroll progress.
                    Mount-gated (never cancels an in-flight load) so its WebGL
                    context only exists near view. */}
                <SceneMount label="laptop" style={{ height: 'min(74vh, 580px)' }}>
                  {(onReady) => <LaptopScene progressRef={storyProgress} onReady={onReady} />}
                </SceneMount>
              </div>
            </div>
          </div>
        ) : (
          <>
        <div style={{ position: 'relative' }}>
          {/* connecting line — draws itself as the steps scroll in */}
          <svg className="steps-line" aria-hidden="true" viewBox="0 0 100 2" preserveAspectRatio="none"
            style={{ position: 'absolute', top: 46, left: '10%', width: '80%', height: 2, zIndex: 0, overflow: 'visible' }}>
            <path d="M0 1 H100" stroke="rgba(200,146,74,0.4)" strokeWidth="1.5" fill="none" vectorEffect="non-scaling-stroke" />
          </svg>
        <div className="steps-grid resp-grid-3" data-reveal-group style={{ position: 'relative', zIndex: 1, display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 16 }}>
          {[
            { n: '01', title: 'Build your profile',   body: "GPA, essays, activities, target schools. The AI reads everything." },
            { n: '02', title: 'Start your interview', body: "Pick a school. The AI takes on that interviewer's persona." },
            { n: '03', title: 'Review your report',   body: 'Every answer scored. Specific things to fix. Practice again.' },
          ].map((step, i) => {
            const on = activeStep === i;
            return (
              <div key={step.n} data-reveal onClick={() => setActiveStep(i)}
                style={{ background: on ? '#08122A' : C.surf, border: `1px solid ${on ? 'rgba(76,130,247,0.4)' : C.line}`, borderRadius: 16, padding: 22, cursor: 'pointer', transition: 'all .22s' }}
                onMouseEnter={e => { if (!on) { e.currentTarget.style.borderColor = 'rgba(76,130,247,0.4)'; e.currentTarget.style.background = '#08122A'; }}}
                onMouseLeave={e => { if (!on) { e.currentTarget.style.borderColor = C.line; e.currentTarget.style.background = C.surf; }}}
              >
                <div style={{ fontFamily: C.serif, fontSize: 48, color: on ? C.blue : '#1A2234', lineHeight: 1, transition: 'color .22s' }}>{step.n}</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: C.t1, margin: '12px 0 6px' }}>{step.title}</div>
                <div style={{ fontSize: 13, color: C.t2, lineHeight: 1.6 }}>{step.body}</div>
              </div>
            );
          })}
        </div>
        </div>
        <div style={{ background: C.surf, border: `1px solid ${C.line}`, borderRadius: 16, overflow: 'hidden', transition: 'opacity .25s' }}>
          {STEP_PANELS[activeStep]}
        </div>
          </>
        )}
      </section>

      {/* ── LIVE MOCKUP ── */}
      <section style={{ padding: '0 28px clamp(112px, 13vw, 152px)', maxWidth: 860, margin: '0 auto' }}>
        <div className="mockup-shell" style={{ background: C.surf, border: '1px solid rgba(255,255,255,0.08)', borderRadius: 18, overflow: 'hidden', willChange: 'transform' }}>
          {/* browser bar */}
          <div style={{ background: '#0D1117', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 7, borderBottom: `1px solid ${C.line}` }}>
            {['#FF5F57', '#FFBD2E', '#28C941'].map(c => (
              <span key={c} style={{ width: 10, height: 10, borderRadius: '50%', background: c, display: 'inline-block' }} />
            ))}
            <span style={{ marginLeft: 8, fontSize: 11, color: C.t3, fontFamily: 'monospace' }}>inveira.site</span>
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: C.green }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.green, display: 'inline-block' }} />
                Live session
              </span>
              <span style={{ fontSize: 11, color: C.t3 }}>Question {mockQNum} of 8</span>
            </div>
          </div>
          {/* two-panel body */}
          <div className="resp-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, background: 'rgba(255,255,255,0.05)' }}>
            {/* left panel */}
            <div style={{ background: C.bg, padding: 22 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#1E3A5F', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, border: '2px solid rgba(76,130,247,0.2)', flexShrink: 0 }}>🎓</div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: C.t1 }}>{NAMES[mockN]}</div>
                  <div style={{ fontSize: 11, color: C.blue, marginTop: 2 }}>Speaking</div>
                </div>
                <div style={{ marginLeft: 'auto', background: 'rgba(229,83,75,0.12)', border: '1px solid rgba(229,83,75,0.25)', borderRadius: 5, padding: '3px 8px', fontSize: 10, fontWeight: 600, color: C.red, letterSpacing: '.04em' }}>REC</div>
              </div>
              <div data-mock-item style={{ background: C.raise, border: `1px solid ${C.line}`, borderRadius: 10, padding: 14, minHeight: 76 }}>
                <div style={{ fontSize: 10, color: C.t3, letterSpacing: '.07em', textTransform: 'uppercase', marginBottom: 8 }}>Current question</div>
                <div style={{ fontSize: 13, color: C.t1, lineHeight: 1.65, transition: 'opacity .3s', opacity: qVisible ? 1 : 0 }}>
                  {QUESTIONS[mockQ]}
                </div>
              </div>
              <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {['Leadership', 'Why Harvard'].map(tag => (
                  <span key={tag} style={{ background: 'rgba(46,194,126,0.1)', color: C.green, border: '1px solid rgba(46,194,126,0.2)', fontSize: 11, fontWeight: 500, padding: '3px 10px', borderRadius: 20 }}>{tag}</span>
                ))}
                <span style={{ background: 'rgba(255,255,255,0.05)', color: C.t3, border: `1px solid ${C.line}`, fontSize: 11, padding: '3px 10px', borderRadius: 20 }}>Major</span>
              </div>
            </div>
            {/* right panel */}
            <div style={{ background: C.bg, padding: 22, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ flex: 1, background: C.raise, border: '2px solid rgba(76,130,247,0.25)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 110 }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#1A2234', margin: '0 auto 7px' }} />
                  <div style={{ fontSize: 11, color: C.t3 }}>Camera preview</div>
                </div>
              </div>
              <div data-mock-item style={{ background: C.raise, border: `1px solid ${C.line}`, borderRadius: 10, padding: 12 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, textAlign: 'center' }}>
                  {[
                    { label: 'Filler', val: mockFill, color: C.green },
                    { label: 'Eye contact', val: `${mockEye}%`, color: C.gold },
                    { label: 'Duration', val: mockDur, color: C.blue },
                  ].map(m => (
                    <div key={m.label}>
                      <div style={{ fontSize: 9, color: C.t3, letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 4 }}>{m.label}</div>
                      <div style={{ fontFamily: C.serif, fontSize: 20, fontWeight: 700, color: m.color }}>{m.val}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={{ flex: 1, background: 'rgba(76,130,247,0.1)', border: '1px solid rgba(76,130,247,0.3)', borderRadius: 8, color: C.blue, fontFamily: C.sans, fontSize: 12, padding: 9, cursor: 'pointer' }}>Stop recording</button>
                <button style={{ padding: '9px 16px', background: C.blue, border: 'none', borderRadius: 8, color: '#fff', fontFamily: C.sans, fontSize: 12, cursor: 'pointer' }}>Next</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── MARQUEE ── */}
      <Marquee />

      {/* ── WHAT MAKES IT DIFFERENT ── */}
      <section style={{ padding: '0 28px clamp(112px, 13vw, 152px)', maxWidth: 860, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <h2 className="wipe-headline" style={{ fontFamily: C.serif, fontSize: 38, color: C.t1, marginBottom: 8 }}>What makes it different</h2>
        </div>
        <div data-reveal-group className="resp-grid-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
          {/* big card */}
          <div data-reveal className="card-lift spot-card" style={{ gridColumn: 'span 2', background: C.surf, border: `1px solid ${C.line}`, borderRadius: 14, padding: 22 }}>
            <div style={{ fontSize: 11, color: C.blue, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 14 }}>Adaptive questions</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: C.t1, marginBottom: 8 }}>Questions that actually follow up</div>
            <div style={{ fontSize: 13, color: C.t2, lineHeight: 1.65, marginBottom: 16 }}>Every answer changes the next question. Mention a project, the AI asks about it. Stumble on a point, it probes deeper. Not a fixed script.</div>
            <div style={{ background: C.raise, border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: 14 }}>
              {[
                { label: 'You say', color: C.t2,  text: 'You mention leading a debate club' },
                { label: 'AI thinks', color: C.blue, text: 'Worth probing deeper' },
                { label: 'AI asks', color: C.gold, text: '"Tell me about a decision your team disagreed with."', italic: true, textColor: C.t1 },
              ].map((row, i, arr) => (
                <div key={row.label} style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
                  <span style={{ fontSize: 11, color: row.color, fontWeight: 500, minWidth: 60 }}>{row.label}</span>
                  <span style={{ fontSize: 12, color: row.textColor || C.t2, fontStyle: row.italic ? 'italic' : 'normal' }}>{row.text}</span>
                </div>
              ))}
            </div>
          </div>
          {/* two stacked cards */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[
              { accent: C.gold, accentLabel: 'Essay cross-check', title: 'AI reads your essays first', body: 'Before every session, the AI reads all your essays. If your spoken answers contradict them, it flags it in the report.' },
              { accent: C.green, accentLabel: 'Per-question scoring', title: 'Every answer scored', body: 'Full transcript, a score, what worked, and a specific fix. Not a vague summary at the end.' },
            ].map(card => (
              <div key={card.accentLabel} data-reveal className="card-lift spot-card" style={{ flex: 1, background: C.surf, border: `1px solid ${C.line}`, borderRadius: 14, padding: 22 }}>
                <div style={{ fontSize: 11, color: card.accent, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 12 }}>{card.accentLabel}</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.t1, marginBottom: 7 }}>{card.title}</div>
                <div style={{ fontSize: 13, color: C.t2, lineHeight: 1.6 }}>{card.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── UNIVERSITIES ── */}
      <section ref={unisRef} data-anchor style={{ padding: '0 28px clamp(112px, 13vw, 152px)', maxWidth: 860, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 10, color: C.t3, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 10 }}>Universities</div>
          <h2 className="wipe-headline" style={{ fontFamily: C.serif, fontSize: 38, color: C.t1, marginBottom: 8 }}>Every school interviews differently.</h2>
          <p style={{ fontSize: 14, color: C.t2 }}>Tailored to each university's interview style — select one to see its profile.</p>
          {/* count-up stats */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 34, marginTop: 22 }}>
            {[
              { to: universities.length, label: 'universities' },
              { to: universities.filter(u => u.type === 'Ivy League').length, label: 'Ivy League' },
              { to: universities.filter(u => u.country === 'UK').length, label: 'UK schools' },
            ].map(s => (
              <div key={s.label} style={{ textAlign: 'center' }}>
                <Counter to={s.to} style={{ fontFamily: C.serif, fontSize: 30, color: C.gold, display: 'block', lineHeight: 1.1 }} />
                <span style={{ fontSize: 11.5, color: C.t3, letterSpacing: '.05em', textTransform: 'uppercase' }}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="uni-grid" style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: 22, alignItems: 'start' }}>
          {/* thermometer */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 6 }}>
            <div style={{ fontSize: 10, color: C.t3, letterSpacing: '.07em', textTransform: 'uppercase', marginBottom: 10, textAlign: 'center', lineHeight: 1.5 }}>Interview<br />Intensity</div>
            <div style={{ position: 'relative', width: 36, height: 260 }}>
              <div style={{ position: 'absolute', inset: 0, background: C.raise, borderRadius: 18, border: '1px solid rgba(255,255,255,0.08)', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: uInfo.color, borderRadius: 18, height: `${uInfo.pct}%`, transition: 'height 1s cubic-bezier(.4,0,.2,1), background 1s' }} />
              </div>
            </div>
            <div style={{ marginTop: 10, fontSize: 14, fontFamily: C.serif, color: uInfo.color, fontWeight: 700, textAlign: 'center', lineHeight: 1.3, transition: 'color 1s' }}>{uInfo.pct}</div>
            <div style={{ fontSize: 10, color: uInfo.color, textAlign: 'center', marginTop: 3, transition: 'color 1s' }}>{uInfo.label}</div>
          </div>
          {/* tabs + search + pills + detail */}
          <div>
            {/* search */}
            <input
              type="text"
              placeholder="Search universities..."
              value={uniSearch}
              onChange={e => { setUniSearch(e.target.value); }}
              style={{ width: '100%', background: C.raise, border: '1px solid rgba(255,255,255,0.09)', borderRadius: 9, color: C.t1, fontSize: 13, height: 36, padding: '0 14px', outline: 'none', boxSizing: 'border-box', marginBottom: 10 }}
            />
            {/* tabs — hidden when searching */}
            {!searchTerm && (
              <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                {['Ivy League', 'Top US', 'UK'].map(tab => {
                  const on = uniTab === tab;
                  return (
                    <button key={tab} onClick={() => { const first = universities.find(u => tab === 'Ivy League' ? u.type === 'Ivy League' : tab === 'Top US' ? (u.country === 'US' && u.type !== 'Ivy League') : u.country === 'UK'); setUniTab(tab); if (first) setActiveUni(first.name); }}
                      style={{ background: on ? 'rgba(76,130,247,0.12)' : 'transparent', border: `1px solid ${on ? 'rgba(76,130,247,0.45)' : 'rgba(255,255,255,0.08)'}`, borderRadius: 8, padding: '6px 14px', color: on ? C.blue : C.t2, fontSize: 12, fontFamily: C.sans, cursor: 'pointer', transition: 'all .15s', whiteSpace: 'nowrap' }}
                      onMouseEnter={e => { if (!on) { e.currentTarget.style.borderColor = 'rgba(76,130,247,0.35)'; e.currentTarget.style.color = C.blue; }}}
                      onMouseLeave={e => { if (!on) { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = C.t2; }}}
                    >{tab}</button>
                  );
                })}
              </div>
            )}
            {/* pills */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 14, maxHeight: 130, overflowY: 'auto' }}>
              {visibleUnis.length === 0 && (
                <span style={{ fontSize: 13, color: C.t3, padding: '6px 0' }}>No universities found.</span>
              )}
              {visibleUnis.map(u => {
                const on = u.name === activeUni;
                return (
                  <button key={u.name} onClick={() => setActiveUni(u.name)}
                    style={{ background: on ? 'rgba(200,146,74,0.07)' : 'transparent', border: `1px solid ${on ? 'rgba(200,146,74,0.45)' : 'rgba(255,255,255,0.08)'}`, borderRadius: 8, padding: '5px 12px', color: on ? C.gold : C.t2, fontSize: 11, fontFamily: C.sans, cursor: 'pointer', transition: 'all .15s', whiteSpace: 'nowrap' }}
                    onMouseEnter={e => { if (!on) { e.currentTarget.style.borderColor = 'rgba(200,146,74,0.45)'; e.currentTarget.style.color = C.gold; e.currentTarget.style.background = 'rgba(200,146,74,0.07)'; }}}
                    onMouseLeave={e => { if (!on) { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = C.t2; e.currentTarget.style.background = 'transparent'; }}}
                  >{u.name}</button>
                );
              })}
            </div>
            {/* detail panel */}
            <div className="card-lift spot-card" style={{ background: C.surf, border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, overflow: 'hidden' }}>
              {/* header row */}
              <div style={{ padding: '18px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                  <div style={{ fontFamily: C.serif, fontSize: 20, color: C.t1 }}>{uData.name}</div>
                  <span style={{ fontSize: 11, color: uInfo.color, background: `${uInfo.color}18`, border: `1px solid ${uInfo.color}33`, borderRadius: 5, padding: '2px 8px' }}>{uInfo.label} intensity</span>
                  {uData.country === 'UK' && <span style={{ fontSize: 11, color: C.blue, background: 'rgba(76,130,247,0.1)', border: '1px solid rgba(76,130,247,0.25)', borderRadius: 5, padding: '2px 8px' }}>UK</span>}
                </div>
                <div style={{ fontSize: 12.5, color: C.t2 }}>{uData.style} · {uData.duration}</div>
              </div>
              {/* type + what inveira does */}
              <div className="resp-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                <div style={{ padding: '16px 20px', borderRight: '1px solid rgba(255,255,255,0.06)' }}>
                  <div style={{ fontSize: 10, color: C.t3, letterSpacing: '.07em', textTransform: 'uppercase', marginBottom: 7 }}>Institution type</div>
                  <div style={{ fontSize: 13, color: C.t2, lineHeight: 1.55 }}>{uData.type}</div>
                  <div style={{ fontSize: 11, color: C.t3, marginTop: 8 }}>{uData.country === 'UK' ? 'British university' : uData.type.includes('Public') ? 'Public university' : 'Private university'}</div>
                </div>
                <div style={{ padding: '16px 20px' }}>
                  <div style={{ fontSize: 10, color: C.t3, letterSpacing: '.07em', textTransform: 'uppercase', marginBottom: 7 }}>Interview style</div>
                  <div style={{ fontSize: 13, color: C.gold, lineHeight: 1.55, borderLeft: `2px solid ${C.gold}`, paddingLeft: 10 }}>{uData.style}</div>
                </div>
              </div>
              {/* what inveira does */}
              <div style={{ padding: '16px 20px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ fontSize: 10, color: C.t3, letterSpacing: '.07em', textTransform: 'uppercase', marginBottom: 10 }}>What Inveira does for {uData.name}</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7 }}>
                  {[`Questions match ${uData.name}'s exact style`, 'Cross-references your essays', 'Adapts follow-ups to what you say', 'Full scored report after every session'].map(feat => (
                    <div key={feat} style={{ display: 'flex', gap: 7, alignItems: 'flex-start' }}>
                      <span style={{ color: C.green, fontSize: 12, marginTop: 1 }}>✓</span>
                      <span style={{ fontSize: 12.5, color: C.t2 }}>{feat}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── GET STARTED / CONTACT (pricing intentionally not shown — billing
             backend is preserved and can be re-enabled later) ── */}
      <section data-anchor style={{ padding: '0 28px clamp(112px, 13vw, 152px)', maxWidth: 860, margin: '0 auto' }}>
        <div data-reveal className="card-lift spot-card" style={{ background: C.surf, border: `1px solid ${C.line}`, borderRadius: 18, padding: '44px 32px', textAlign: 'center' }}>
          <div style={{ fontSize: 10, color: C.t3, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 10 }}>Get started</div>
          <h2 className="wipe-headline" style={{ fontFamily: C.serif, fontSize: 36, color: C.t1, marginBottom: 12 }}>Start practicing for free.</h2>
          <p style={{ fontSize: 15, color: C.t2, lineHeight: 1.7, maxWidth: 520, margin: '0 auto 26px' }}>
            Create a free account and run your first interview in minutes. Interested in Inveira for your school or organization? We&apos;d love to hear from you.
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="btn-anim" style={{ ...btnP, padding: '11px 26px', borderRadius: 10 }} onClick={() => navigate('/signup')}
              onMouseEnter={e => { e.currentTarget.style.background = '#3568D4'; }}
              onMouseLeave={e => { e.currentTarget.style.background = C.blue; }}
            >Start free</button>
            <button className="btn-anim" style={{ ...btnG, padding: '11px 26px', borderRadius: 10 }}
              onClick={() => { window.location.href = 'mailto:somaanfater@gmail.com?subject=Inveira%20for%20schools%20%26%20organizations'; }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.22)'; e.currentTarget.style.color = C.t1; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';  e.currentTarget.style.color = C.t2; }}
            >Contact us</button>
          </div>
        </div>
      </section>

      {/* ── FOR SCHOOLS ── */}
      <section ref={schoolsRef} data-anchor style={{ padding: '0 28px clamp(112px, 13vw, 152px)', maxWidth: 860, margin: '0 auto' }}>
        <div data-reveal-group className="resp-grid-2" style={{ background: C.surf, border: `1px solid ${C.line}`, borderRadius: 18, padding: 36, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 36, alignItems: 'center' }}>
          <div data-reveal>
            <div style={{ fontSize: 10, color: C.t3, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 12 }}>For schools</div>
            <h2 style={{ fontFamily: C.serif, fontSize: 30, color: C.t1, marginBottom: 14, lineHeight: 1.2 }}>Give every student a real practice run.</h2>
            <p style={{ fontSize: 14, color: C.t2, lineHeight: 1.75, marginBottom: 20 }}>One counselor account. Assign universities, set deadlines, track every student's progress from a single dashboard.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginBottom: 22 }}>
              <BulletCheck text="Student roster with scores and trends" />
              <BulletCheck text="Bulk assign universities and practice deadlines" />
              <BulletCheck text="Read student reports without accessing accounts" />
              <BulletCheck text="School keys for instant student onboarding" />
            </div>
            <button style={{ ...btnP, padding: '11px 24px', borderRadius: 10 }}
              onClick={() => { window.location.href = 'mailto:somaanfater@gmail.com'; }}
              onMouseEnter={e => { e.currentTarget.style.background = '#3568D4'; }}
              onMouseLeave={e => { e.currentTarget.style.background = C.blue; }}
            >Contact sales</button>
          </div>
          {/* roster preview */}
          <div data-reveal style={{ background: C.raise, border: `1px solid ${C.line}`, borderRadius: 14, padding: 20 }}>
            <div style={{ fontSize: 11, color: C.t3, letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 14 }}>Student roster preview</div>
            {ROSTER.map((r, i) => {
              const sc = r.score >= 80 ? C.green : r.score >= 65 ? C.warn : C.red;
              const tc = r.trend.startsWith('+') ? C.green : C.red;
              return (
                <div key={r.name} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: i < ROSTER.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'rgba(76,130,247,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: C.blue, fontWeight: 600, flexShrink: 0 }}>{r.name[0]}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, color: C.t1 }}>{r.name}</div>
                    <div style={{ fontSize: 11, color: C.t3 }}>{r.uni}</div>
                  </div>
                  <div style={{ fontFamily: C.serif, fontSize: 18, fontWeight: 700, color: sc }}>{r.score}</div>
                  <div style={{ fontSize: 12, color: tc, minWidth: 26, textAlign: 'right' }}>{r.trend}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <div style={{ borderTop: `1px solid ${C.line}`, padding: '22px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <InveiraLogo size={18} dark={true} />
          <span style={{ fontFamily: C.serif, fontSize: 17, color: C.gold }}>Inveira</span>
        </div>
        <div style={{ display: 'flex', gap: 22 }}>
          <span style={{ fontSize: 12, color: C.t3, cursor: 'pointer' }} onClick={() => setPolicyModal('privacy')}>Privacy</span>
          <span style={{ fontSize: 12, color: C.t3, cursor: 'pointer' }} onClick={() => setPolicyModal('terms')}>Terms</span>
          <a href="mailto:somaanfater@gmail.com" style={{ fontSize: 12, color: C.t3, textDecoration: 'none' }}>Contact</a>
        </div>
        <span style={{ fontSize: 12, color: C.t3 }}>© 2026 Inveira</span>
      </div>

      {policyModal && <PolicyModal type={policyModal} onClose={() => setPolicyModal(null)} />}
    </div>
  );
}
